import React, { useCallback, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

Chart.register(...registerables);

interface WasteGHGChartProps {
  location: string;
  embedOnly?: boolean;
}

const WasteGHGChart: React.FC<WasteGHGChartProps> = ({ location, embedOnly = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);
  const creatingRef = useRef<boolean>(false);

  const buildChart = useCallback(async () => {
    if (!canvasRef.current) return;
    if (creatingRef.current) return;
    creatingRef.current = true;
    try {
      if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
      const existing = Chart.getChart(canvasRef.current as unknown as HTMLCanvasElement);
      if (existing) existing.destroy();
    } catch {}

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    await supabaseLocationService.initialize();
    const resolvedGeofips = await supabaseLocationService.getGeofips(location);
    
    if (!resolvedGeofips) {
      console.warn('WasteGHGChart: No geofips for location:', location);
      creatingRef.current = false;
      return;
    }

    // Get location info to determine if it's a city or county
    // For landfill data, we need county-level geofips
    const { data: locationData } = await supabaseClient
      .from('population')
      .select('geography_type, county')
      .eq('geofips', resolvedGeofips)
      .limit(1);

    let countyGeofips = resolvedGeofips;
    
    // If it's a city, try to get county geofips for landfill data
    if (locationData && locationData.length > 0 && locationData[0].geography_type === 'City') {
      const countyName = locationData[0].county;
      if (countyName) {
        const { data: countyData } = await supabaseClient
          .from('population')
          .select('geofips')
          .eq('location_name', countyName)
          .eq('geography_type', 'County')
          .limit(1);
        
        if (countyData && countyData.length > 0) {
          countyGeofips = countyData[0].geofips;
        }
      }
    }

    // Fetch waste tons from total_waste for the selected location
    const { data: wasteData, error: wasteError } = await supabaseClient
      .from('total_waste')
      .select('year, tons_waste')
      .eq('geofips', resolvedGeofips)
      .order('year', { ascending: true });

    if (wasteError) {
      console.warn('WasteGHGChart: Error fetching total_waste:', wasteError);
    }

    // Fetch landfill emissions data at county level to calculate emission factors
    const { data: landfillData, error: landfillError } = await supabaseClient
      .from('landfill_emissions_full')
      .select('year, waste_tons, ghg_emissions')
      .eq('geofips', countyGeofips)
      .order('year', { ascending: true });

    if (landfillError) {
      console.warn('WasteGHGChart: Error fetching landfill_emissions_full:', landfillError);
    }

    // Calculate county-specific emission factors by year
    // SUM(GHG Emissions) per county and year / SUM(waste_tons) per county and year
    const countyTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
    (landfillData || []).forEach((r: any) => {
      const year = r.year;
      if (!countyTotalsByYear[year]) {
        countyTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
      }
      countyTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
      countyTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
    });

    // Calculate emission factor (carbon intensity) for each year
    const emissionFactorsByYear: Record<number, number> = {};
    Object.keys(countyTotalsByYear).forEach(yearStr => {
      const year = parseInt(yearStr);
      const totals = countyTotalsByYear[year];
      if (totals.totalWaste > 0) {
        emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
      }
    });

    // Check if we have valid county emission factors, if not fetch statewide average by aggregating all California counties
    const hasValidCountyFactors = Object.keys(emissionFactorsByYear).length > 0 && 
      Object.values(emissionFactorsByYear).some(factor => factor > 0);
    
    if (!hasValidCountyFactors) {
      console.log('WasteGHGChart: No valid county emission factors, calculating statewide average from all California counties');
      
      // Fetch all California county data (geofips starting with 60, typically 4-digit county codes)
      // This aggregates all counties to get California-wide averages
      const { data: californiaLandfillData, error: californiaError } = await supabaseClient
        .from('landfill_emissions_full')
        .select('year, waste_tons, ghg_emissions, geofips')
        .gte('geofips', 6000)
        .lt('geofips', 7000) // California counties are typically 6000-6999
        .order('year', { ascending: true });

      if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
        // Calculate statewide emission factors by aggregating all counties
        const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
        californiaLandfillData.forEach((r: any) => {
          const year = r.year;
          if (!stateTotalsByYear[year]) {
            stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
          }
          stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
          stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
        });

        // Calculate statewide emission factors
        Object.keys(stateTotalsByYear).forEach(yearStr => {
          const year = parseInt(yearStr);
          const totals = stateTotalsByYear[year];
          if (totals.totalWaste > 0) {
            emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
          }
        });
        
        console.log('WasteGHGChart: Using statewide average emission factors calculated from all California counties');
        console.log(`WasteGHGChart: Calculated factors for ${Object.keys(emissionFactorsByYear).length} years`);
      } else {
        console.warn('WasteGHGChart: Error or no data fetching California statewide emission factors:', californiaError);
        console.warn('WasteGHGChart: California data length:', californiaLandfillData?.length || 0);
      }
    } else {
      // If we have some county factors but not all years, fill missing years with statewide average
      const allRequiredYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
      const missingYears = allRequiredYears.filter(year => !emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0);
      
          if (missingYears.length > 0) {
            console.log(`WasteGHGChart: Filling ${missingYears.length} missing years with statewide average`);
            
            // Fetch all California county data to calculate statewide averages
            const { data: californiaLandfillData, error: californiaError } = await supabaseClient
              .from('landfill_emissions_full')
              .select('year, waste_tons, ghg_emissions, geofips')
              .gte('geofips', 6000)
              .lt('geofips', 7000) // California counties are typically 6000-6999
              .order('year', { ascending: true });

        if (!californiaError && californiaLandfillData && californiaLandfillData.length > 0) {
          // Calculate statewide emission factors for missing years
          const stateTotalsByYear: Record<number, { totalGHG: number; totalWaste: number }> = {};
          californiaLandfillData.forEach((r: any) => {
            const year = r.year;
            if (missingYears.includes(year)) {
              if (!stateTotalsByYear[year]) {
                stateTotalsByYear[year] = { totalGHG: 0, totalWaste: 0 };
              }
              stateTotalsByYear[year].totalGHG += Number(r.ghg_emissions || 0);
              stateTotalsByYear[year].totalWaste += Number(r.waste_tons || 0);
            }
          });

          // Fill in missing years with statewide factors
          Object.keys(stateTotalsByYear).forEach(yearStr => {
            const year = parseInt(yearStr);
            const totals = stateTotalsByYear[year];
            if (totals.totalWaste > 0 && (!emissionFactorsByYear[year] || emissionFactorsByYear[year] === 0)) {
              emissionFactorsByYear[year] = totals.totalGHG / totals.totalWaste;
            }
          });
        }
      }
    }

    // Project carbon intensity linearly for years 2020-2023 if data not available
    const yearsWithFactors = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
    if (yearsWithFactors.length > 0) {
      const latestYearWithFactor = Math.max(...yearsWithFactors);
      const lastYear = latestYearWithFactor;
      const lastFactor = emissionFactorsByYear[lastYear];
      
      // If latest year is before 2023, project forward
      if (latestYearWithFactor < 2023) {
        // Find the last two years with data for linear projection
        const secondLastYear = yearsWithFactors[yearsWithFactors.length - 2];
        
        if (secondLastYear && secondLastYear < lastYear) {
          const secondLastFactor = emissionFactorsByYear[secondLastYear];
          
          // Calculate trend per year
          const yearsDiff = lastYear - secondLastYear;
          const factorDiff = lastFactor - secondLastFactor;
          const trendPerYear = factorDiff / yearsDiff;
          
          // Project for years 2020-2023 if missing
          for (let year = 2020; year <= 2023; year++) {
            if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
              const yearsToProject = year - lastYear;
              emissionFactorsByYear[year] = lastFactor + (trendPerYear * yearsToProject);
            }
          }
        } else {
          // If only one year of data, use that value for all missing years
          for (let year = 2020; year <= 2023; year++) {
            if (!emissionFactorsByYear[year] || year > latestYearWithFactor) {
              emissionFactorsByYear[year] = lastFactor;
            }
          }
        }
      }
    }

    // Fetch population data for backcasting waste tons
    const { data: populationData } = await supabaseClient
      .from('population')
      .select('year, population')
      .eq('geofips', resolvedGeofips)
      .order('year', { ascending: true });

    // Build population by year
    const populationByYear: Record<number, number> = {};
    (populationData || []).forEach((r: any) => {
      populationByYear[r.year] = Number(r.population || 0);
    });

    // Aggregate waste tons by year for the selected location
    const wasteTonsByYear: Record<number, number> = {};
    (wasteData || []).forEach((r: any) => {
      const year = r.year;
      wasteTonsByYear[year] = (wasteTonsByYear[year] || 0) + Number(r.tons_waste || 0);
    });

    // Backcast waste tons to 2005 using population ratios
    const wasteYears = Object.keys(wasteTonsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
    const earliestWasteYear = wasteYears.length > 0 ? Math.min(...wasteYears) : null;

    if (earliestWasteYear && earliestWasteYear > 2005 && populationByYear[earliestWasteYear] && populationByYear[earliestWasteYear] > 0) {
      const baseTons = wasteTonsByYear[earliestWasteYear];
      const basePopulation = populationByYear[earliestWasteYear];

      // Backcast years before earliest waste data year
      for (let year = 2005; year < earliestWasteYear; year++) {
        const yearPopulation = populationByYear[year] || basePopulation;
        const populationRatio = yearPopulation / basePopulation;
        
        // Backcast tons using population ratio
        wasteTonsByYear[year] = baseTons * populationRatio;
      }
    }

    // Backcast emission factors to 2005 if needed
    const factorYears = Object.keys(emissionFactorsByYear).map(y => parseInt(y)).sort((a, b) => a - b);
    const earliestFactorYear = factorYears.length > 0 ? Math.min(...factorYears) : null;

    if (earliestFactorYear && earliestFactorYear > 2005) {
      const baseFactor = emissionFactorsByYear[earliestFactorYear];
      
      // Use earliest factor for all years before earliest data year
      for (let year = 2005; year < earliestFactorYear; year++) {
        emissionFactorsByYear[year] = baseFactor;
      }
    }

    // Calculate GHG emissions = waste_tons × emission_factor for each year (2005-2023)
    const calculatedEmissionsByYear: Record<number, number> = {};
    const allYears = Array.from({ length: 2023 - 2005 + 1 }, (_, i) => 2005 + i);
    
    allYears.forEach(year => {
      const wasteTons = wasteTonsByYear[year] || 0;
      const emissionFactor = emissionFactorsByYear[year] || 0;
      
      if (wasteTons > 0 && emissionFactor > 0) {
        calculatedEmissionsByYear[year] = wasteTons * emissionFactor;
      } else {
        calculatedEmissionsByYear[year] = 0;
      }
    });

    const years = allYears;
    const totalByYear: Record<number, number> = {};
    years.forEach(year => {
      totalByYear[year] = calculatedEmissionsByYear[year] || 0;
    });

    // Determine which years are backcasted (extrapolated) for waste tons and emissions
    const extrapolatedWasteEndYear = earliestWasteYear && earliestWasteYear > 2005 ? earliestWasteYear - 1 : null;

    // Determine extrapolated years for emissions (use the later of waste or factor extrapolation)
    const extrapolatedFactorStartYear = earliestFactorYear && earliestFactorYear > 2005 ? 2005 : null;
    const extrapolatedFactorEndYear = earliestFactorYear && earliestFactorYear > 2005 ? earliestFactorYear - 1 : null;
    
    // Use the earliest extrapolation end year (most conservative - shows more as extrapolated)
    const extrapolatedEmissionsEndYear = extrapolatedWasteEndYear && extrapolatedFactorEndYear
      ? Math.max(extrapolatedWasteEndYear, extrapolatedFactorEndYear)
      : extrapolatedWasteEndYear || extrapolatedFactorEndYear;
    const extrapolatedEmissionsStartYear = extrapolatedEmissionsEndYear ? 2005 : null;

    // Create datasets with extrapolated portions shown as dotted lines
    const emissionsData = years.map(y => totalByYear[y] || 0);
    const intensityData = years.map(y => emissionFactorsByYear[y] || 0);
    
    const datasets: any[] = [];
    
    // Landfill GHG Emissions - split into extrapolated and actual
    if (extrapolatedEmissionsStartYear !== null && extrapolatedEmissionsEndYear !== null) {
      // Extrapolated portion (dotted) - include transition year to connect seamlessly
      const extrapolatedEmissions = years.map((year, idx) => {
        if (year >= extrapolatedEmissionsStartYear && year <= extrapolatedEmissionsEndYear + 1) {
          return emissionsData[idx];
        }
        return null;
      });
      datasets.push({
        label: 'Landfill GHG Emissions (Extrapolated)',
        data: extrapolatedEmissions,
        borderColor: '#ef4444',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
      });
      
      // Actual data portion (solid) - include transition year to connect seamlessly
      const actualEmissions = years.map((year, idx) => {
        if (year <= extrapolatedEmissionsEndYear) {
          return null;
        }
        return emissionsData[idx];
      });
      
      // Add transition point to actual data for seamless connection
      const transitionYearIdx = years.indexOf(extrapolatedEmissionsEndYear + 1);
      if (transitionYearIdx >= 0 && transitionYearIdx < actualEmissions.length) {
        actualEmissions[transitionYearIdx] = emissionsData[transitionYearIdx];
      }
      datasets.push({
        label: 'Landfill GHG Emissions',
        data: actualEmissions,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.25)',
        tension: 0.2,
        pointRadius: 0,
        fill: true,
      });
    } else {
      // No extrapolation, show all as solid
      datasets.push({
        label: 'Landfill GHG Emissions',
        data: emissionsData,
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239,68,68,0.25)',
        tension: 0.2,
        pointRadius: 0,
        fill: true,
      });
    }
    
    // Carbon Intensity (always dashed, but split if extrapolated)
    if (extrapolatedFactorStartYear !== null && extrapolatedFactorEndYear !== null) {
      // Extrapolated portion - include transition year to connect seamlessly
      const extrapolatedIntensity = years.map((year, idx) => {
        if (year >= extrapolatedFactorStartYear && year <= extrapolatedFactorEndYear + 1) {
          return intensityData[idx];
        }
        return null;
      });
      datasets.push({
        label: 'Carbon Intensity (Extrapolated)',
        data: extrapolatedIntensity,
        borderColor: '#f59e0b',
        backgroundColor: 'transparent',
        borderDash: [10, 5], // Different dash pattern to distinguish from extrapolated emissions
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: 'y1',
      });
      
      // Actual intensity data - include transition year to connect seamlessly
      const actualIntensity = years.map((year, idx) => {
        if (year <= extrapolatedFactorEndYear) {
          return null;
        }
        return intensityData[idx];
      });
      
      // Add transition point to actual data for seamless connection
      const transitionYearIdx = years.indexOf(extrapolatedFactorEndYear + 1);
      if (transitionYearIdx >= 0 && transitionYearIdx < actualIntensity.length) {
        actualIntensity[transitionYearIdx] = intensityData[transitionYearIdx];
      }
      datasets.push({
        label: 'Carbon Intensity (tons CO₂e per ton waste)',
        data: actualIntensity,
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245,158,11,0.1)',
        borderDash: [5, 5],
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: 'y1',
      });
    } else {
      // No extrapolation for intensity
      datasets.push({
        label: 'Carbon Intensity (tons CO₂e per ton waste)',
        data: intensityData,
        borderColor: '#f59e0b',
        backgroundColor: 'rgba(245,158,11,0.1)',
        borderDash: [5, 5],
        tension: 0.2,
        pointRadius: 0,
        borderWidth: 2,
        yAxisID: 'y1',
      });
    }

    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: years,
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          title: { display: true, text: `Waste GHG Emissions (${location})`, font: { size: 16, weight: 'bold' } },
        },
        scales: {
          y: { 
            beginAtZero: true, 
            position: 'left',
            title: { display: true, text: 'Emissions (Metric Tons CO₂e)' } 
          },
          y1: {
            type: 'linear',
            position: 'right',
            grid: { drawOnChartArea: false },
            title: { display: true, text: 'Carbon Intensity (tons CO₂e/ton waste)' },
            beginAtZero: true,
          },
          x: { title: { display: true, text: 'Year' } },
        },
        interaction: { mode: 'index', intersect: false },
      },
    });
    creatingRef.current = false;
  }, [location]);

  useEffect(() => {
    buildChart();
    const canvasNode = canvasRef.current;
    return () => {
      try {
        if (chartRef.current) chartRef.current.destroy();
        if (canvasNode) {
          const existing = Chart.getChart(canvasNode as unknown as HTMLCanvasElement);
          if (existing) existing.destroy();
        }
      } catch {}
    };
  }, [buildChart]);

  return (
    <div className="chart-container" style={{ height: embedOnly ? '100%' : '400px', width: '100%' }}>
      <canvas ref={canvasRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );
};

export default WasteGHGChart;
